import AppState, { contactLabelSingular } from '@/states/app'
import type { EventNotification } from '@/states/EventNotificationState'
import type { NotificationConfig } from '@/types/notification'
import {
  collectInvalidTemplateVariableFields,
  getDedupedTemplateVariableSnippets,
  resolveTemplateVariableWarningKind,
} from '@/utils/templateValidator'
import type { TemplateVariableValidationBannerState } from './templateVariableValidationBannerState'

export type NotificationTranslateFn = (
  key: string,
  params?: Record<string, unknown>
) => string

export type NotificationMergeFieldInput = {
  text: string
  isHtml?: boolean
  fieldLabel: string
}

export type SmsTemplateOption = {
  value: string
  body?: string
  template?: { body?: string }
}

const getSmsTemplateBody = (template: SmsTemplateOption) =>
  template.body || template.template?.body || ''

export function getEmailReceiverLabel(
  receiverType: string,
  t: NotificationTranslateFn
): string {
  const labels: Record<string, string> = {
    contact: contactLabelSingular.value,
    guest: t('eventNotification.guest'),
    assignedUser: t('eventNotification.assignedUser'),
    emails: t('eventNotification.additionalEmails'),
  }
  return labels[receiverType] ?? receiverType
}

export function getSmsReceiverLabel(
  receiverType: string,
  t: NotificationTranslateFn
): string {
  const labels: Record<string, string> = {
    contact: contactLabelSingular.value,
    guest: t('eventNotification.guest'),
    assignedUser: t('eventNotification.assignedUser'),
    phoneNumbers: t('eventNotification.additionalEmails'),
    emails: t('eventNotification.additionalEmails'),
  }
  return labels[receiverType] ?? receiverType
}

export function buildMeetingsEmailMergeFieldInputs(
  eventNotification: EventNotification,
  t: NotificationTranslateFn
): Array<{ text: string; isHtml?: boolean; fieldLabel: string }> {
  const s = eventNotification.state
  const headerFields = [
    {
      text: s.subject || '',
      fieldLabel: t('eventNotification.email.templateVariableFieldSubject'),
    },
    {
      text: s.fromName || '',
      fieldLabel: t('eventNotification.email.templateVariableFieldFromName'),
    },
    {
      text: s.fromAddress || '',
      fieldLabel: t('eventNotification.email.templateVariableFieldFromAddress'),
    },
  ]

  const usesCustomBody =
    eventNotification.actionTemplate.value?.attributes?.template_id === undefined

  if (!usesCustomBody) {
    return headerFields
  }

  return [
    ...headerFields,
    {
      text: s.body || '',
      isHtml: true,
      fieldLabel: t('eventNotification.email.templateVariableFieldEmailBody'),
    },
  ]
}

export function getSmsTemplateOptionsForValidation(): SmsTemplateOption[] {
  const templates = AppState.smsTemplates
  return Array.isArray(templates) ? templates : []
}

export function buildMeetingsSmsMergeFieldInputs(
  config: NotificationConfig,
  templateOptions: SmsTemplateOption[],
  t: NotificationTranslateFn
): Array<{ text: string; fieldLabel: string }> {
  let effectiveBody = config.body || ''
  const templateId = config.templateId
  if (templateId && templateId !== 'none') {
    const template = templateOptions.find(option => option.value === templateId)
    effectiveBody = template ? getSmsTemplateBody(template) : effectiveBody
  }

  return [
    {
      text: effectiveBody,
      fieldLabel: t('eventNotification.sms.templateVariableFieldSmsBody'),
    },
  ]
}

export function buildTemplateVariableBannerState(
  fields: Array<{ text: string; isHtml?: boolean; fieldLabel: string }>,
  t: NotificationTranslateFn
): TemplateVariableValidationBannerState {
  const fieldValidations = collectInvalidTemplateVariableFields(fields)
  if (!fieldValidations.length) {
    return null
  }

  const kind = resolveTemplateVariableWarningKind(fieldValidations)
  const snippets = getDedupedTemplateVariableSnippets(fieldValidations)

  if (kind === 'markup') {
    return {
      type: 'plain',
      text: t('eventNotification.templateVariableIssueMarkup'),
    }
  }
  if (kind === 'empty') {
    return {
      type: 'plain',
      text: t('eventNotification.templateVariableIssueEmpty', {
        emptyPair: '{{ }}',
      }),
    }
  }
  if (kind === 'single') {
    return {
      type: 'plain',
      text: t('eventNotification.templateVariableIssueSingle', {
        value: snippets[0] ?? '',
      }),
    }
  }
  return {
    type: 'default',
    invalidSnippets: snippets,
  }
}

export function passesTemplateVariableFieldValidation(
  fields: Array<{ text: string; isHtml?: boolean; fieldLabel: string }>
): boolean {
  return collectInvalidTemplateVariableFields(fields).length === 0
}

export function formatTemplateVariableBannerMessage(
  banner: TemplateVariableValidationBannerState,
  t: NotificationTranslateFn
): string {
  if (!banner) {
    return t('eventNotification.templateVariableSaveFailedGeneric')
  }
  if (banner.type === 'plain') {
    return banner.text
  }
  return `${t('eventNotification.templateVariableIssueDefaultPartBefore')}${t('eventNotification.templateVariableIssueDefaultPartHighlight')}${t('eventNotification.templateVariableIssueDefaultPartAfter')}`
}

export function buildReceiverTemplateVariableSaveError(
  receiverLabel: string,
  fields: NotificationMergeFieldInput[],
  t: NotificationTranslateFn
): string | null {
  if (passesTemplateVariableFieldValidation(fields)) {
    return null
  }
  return t('eventNotification.templateVariableSaveFailed', { receiverLabel })
}

export type NotificationSaveValidationResult = {
  results: boolean[]
  errorMessage: string | null
  failedReceiverIndex: number | null
}
